/**
 * AuthProvider — the React context wrapper that lazily wires MSAL into
 * the v2 add-in.
 *
 * v1 instantiated `PublicClientApplication` at module-load time, which
 * pulled the entire MSAL bundle into the cold-start path even when the
 * Tier 2 (Office Dialog) happy path was the only one that ever fired.
 * In v2 the cascade is Tier 1 (Office SSO) → Tier 2 (Dialog) → Tier 3
 * (MSAL); the first two have zero MSAL cost. `AuthProvider` therefore
 * only instantiates `PublicClientApplication` and mounts `MsalProvider`
 * once Tier 3 is actually invoked via `ensureMsalInstance()`.
 *
 * Tests verify that:
 *   - Mounting <AuthProvider> alone does NOT instantiate MSAL.
 *   - Calling `ensureMsalInstance()` instantiates exactly once and
 *     subsequent calls return the same instance.
 */

import React from "react";
import { MsalProvider } from "@azure/msal-react";
import {
  EventType,
  type AccountInfo,
  type IPublicClientApplication,
  type PublicClientApplication as PCA,
} from "@azure/msal-browser";
import { msalConfig } from "./msalConfig";
import { logger } from "../obs/logger";

let _msalInstance: IPublicClientApplication | null = null;
let _initPromise: Promise<IPublicClientApplication> | null = null;

/**
 * Lazily construct the MSAL `PublicClientApplication`. Returns the same
 * instance on every subsequent call. Idempotent. Safe to invoke from
 * multiple call sites concurrently — the single in-flight promise is
 * shared.
 */
export async function ensureMsalInstance(): Promise<IPublicClientApplication> {
  if (_msalInstance) return _msalInstance;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    logger.info("Tier 3 MSAL: instantiating PublicClientApplication", {
      auth_tier: "msal_silent",
    });
    // Dynamic import so the MSAL bundle is split out of the cold-start
    // chunk. Tier 1 + Tier 2 happy path never loads MSAL.
    const mod = await import("@azure/msal-browser");
    const PublicClientApplication = mod.PublicClientApplication as typeof PCA;
    const instance = new PublicClientApplication(msalConfig);
    await instance.initialize();
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const account = (event.payload as { account: AccountInfo }).account;
        instance.setActiveAccount(account);
      }
    });
    _msalInstance = instance;
    return instance;
  })();

  try {
    return await _initPromise;
  } finally {
    // Don't clear _initPromise on success — we want the cached instance.
    // Only clear on failure so a retry can succeed.
    if (!_msalInstance) {
      _initPromise = null;
    }
  }
}

/** Test helper — reset the lazy singleton between cases. */
export function _resetMsalForTests(): void {
  _msalInstance = null;
  _initPromise = null;
}

/** Synchronously read the cached instance, or null if not yet initialised. */
export function peekMsalInstance(): IPublicClientApplication | null {
  return _msalInstance;
}

/**
 * MsalProviderGate — only wraps children with `MsalProvider` once the
 * MSAL instance exists. While Tier 1 / Tier 2 are the only fired tiers,
 * children render unwrapped and the MSAL React hooks (`useMsal`) will
 * throw if called — that's fine because Tier 3 is the only consumer of
 * those hooks and it only runs after `ensureMsalInstance()` succeeds.
 */
const MsalProviderGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [instance, setInstance] = React.useState<IPublicClientApplication | null>(
    () => peekMsalInstance(),
  );

  React.useEffect(() => {
    let mounted = true;
    // Subscribe to instance creation. We don't auto-init — useAuth's
    // Tier 3 cascade triggers it. Poll once per second until present;
    // negligible overhead because polling stops the moment the instance
    // is set.
    const interval = window.setInterval(() => {
      const peek = peekMsalInstance();
      if (peek && mounted) {
        setInstance(peek);
      }
    }, 1000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, []);

  if (!instance) {
    return <>{children}</>;
  }
  return <MsalProvider instance={instance}>{children}</MsalProvider>;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <MsalProviderGate>{children}</MsalProviderGate>;
};
